import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, CircularProgress, Stack } from '@mui/material';
import React from 'react';
import { SubtleOutlineButton } from './SubtleOutlineButton.styles';
import {
  campaignInboxLines,
  messageTypeLines,
} from '../../helpers';
import { ChartConfig, ChartType, Granularity, MetricChartData, TimePeriod } from '../../types';
import GranularitySelector from '../GranularitySelector';
import CreditsChartCard from './CreditsChartCard';
import DateRangePicker from './DateRangePicker';
import LinePieChartCard from './LinePieChartCard';

export default function AnalyticsSection({
  timePeriod,
  onPresetChange,
  customOpen,
  customAnchorEl,
  onCustomOpen,
  onCustomClose,
  startDateInterval,
  endDateInterval,
  onCustomRangeChange,
  granularity,
  onGranularityChange,
  metricsLoading,
  chartData,
  creditChartConfig,
  messageTypeConfig,
  campaignInboxConfig,
  onCreditTypeChange,
  onMessageTypeChange,
  onCampaignInboxTypeChange,
  customLabel,
}: {
  timePeriod: TimePeriod;
  onPresetChange: (period: TimePeriod) => void;
  customOpen: boolean;
  customAnchorEl: HTMLElement | null;
  onCustomOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onCustomClose: () => void;
  startDateInterval: string | null;
  endDateInterval: string | null;
  onCustomRangeChange: (start: string, end: string) => void;
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  metricsLoading: boolean;
  chartData: MetricChartData[];
  creditChartConfig: ChartConfig;
  messageTypeConfig: ChartConfig;
  campaignInboxConfig: ChartConfig;
  onCreditTypeChange: (type: ChartType) => void;
  onMessageTypeChange: (type: ChartType) => void;
  onCampaignInboxTypeChange: (type: ChartType) => void;
  customLabel: string;
}) {
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        mb={2}
      >
        <GranularitySelector value={granularity} onChange={onGranularityChange} />
        <SubtleOutlineButton
          variant="outlined"
          size="small"
          startIcon={<CalendarTodayIcon fontSize="small" />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={onCustomOpen}
          sx={{ height: 35 }}
        >
          {customLabel}
        </SubtleOutlineButton>
      </Stack>

      <DateRangePicker
        open={customOpen}
        anchorEl={customAnchorEl}
        onClose={onCustomClose}
        timePeriod={timePeriod}
        startDateInterval={startDateInterval}
        endDateInterval={endDateInterval}
        onPresetChange={onPresetChange}
        onCustomRangeChange={onCustomRangeChange}
      />

      {metricsLoading && (
        <Stack direction="row">
          <CircularProgress />
        </Stack>
      )}

      {!metricsLoading && (
        <Stack spacing={2} mt={2}>
          <CreditsChartCard
            data={chartData}
            type={creditChartConfig.type}
            onTypeChange={onCreditTypeChange}
          />
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} mb={3}>
            <Box flex={1} minWidth={0}>
              <LinePieChartCard
                title="Message Type"
                lines={messageTypeLines}
                data={chartData}
                type={messageTypeConfig.type}
                onTypeChange={onMessageTypeChange}
              />
            </Box>
            <Box flex={1} minWidth={0}>
              <LinePieChartCard
                title="Campaign vs Inbox Messages"
                lines={campaignInboxLines}
                data={chartData}
                type={campaignInboxConfig.type}
                onTypeChange={onCampaignInboxTypeChange}
              />
            </Box>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
